import React, { useEffect, useState } from 'react';
import css from '../Input/text-input.module.scss';
import FormError from '../FormError/FormError';
import { Input } from '@mui/base/Input';
import clsx from 'clsx';

const UNIT_ORDER = ['h', 'm'] as const; // from biggest to smallest

type TimeUnit = (typeof UNIT_ORDER)[number];

const SECONDS_IN: Record<TimeUnit, number> = {
  h: 3600,
  m: 60,
};

const parseDuration = (input: string): number | null => {
  const regex = /(\d+)\s*([hm])/gi;
  let match;
  let totalSeconds = 0;
  let usedUnits: TimeUnit[] = [];

  // Remove leading/trailing spaces and normalize spaces for easier check
  const normalized = input.trim().replace(/\s+/g, ' ');

  // Extract all matched tokens
  const matchedTokens: string[] = [];
  while ((match = regex.exec(normalized)) !== null) {
    matchedTokens.push(match[0].trim());

    const value = parseInt(match[1], 10);
    const unit = match[2].toLowerCase() as TimeUnit;

    // Duplicate unit check
    if (usedUnits.includes(unit)) return null;

    // Unit order check
    if (
      usedUnits.length > 0 &&
      UNIT_ORDER.indexOf(unit) <=
        UNIT_ORDER.indexOf(usedUnits[usedUnits.length - 1])
    ) {
      return null;
    }

    usedUnits.push(unit);

    totalSeconds += value * SECONDS_IN[unit];
  }

  if (matchedTokens.join('') !== normalized.replace(' ', '')) {
    return null;
  }

  return usedUnits.length > 0 ? totalSeconds : null;
};

const sanitizeInput = (value: string): string => {
  // Remove all but digits, w/d/h/m, and spaces
  return value.replace(/[^0-9hm\s]/gi, '');
};

const formatDuration = (seconds: number): string => {
  let remaining = seconds;
  const parts: string[] = [];

  for (const unit of UNIT_ORDER) {
    const unitSeconds = SECONDS_IN[unit];
    const count = Math.floor(remaining / unitSeconds);
    if (count > 0) {
      parts.push(`${count}${unit}`);
      remaining -= count * unitSeconds;
    }
  }

  return parts.join(' ');
};

type ValueInSeconds = number;

type Props = {
  placeholder?: string;
  autoFocus?: boolean;
  value?: ValueInSeconds;
  onChange?(value: ValueInSeconds | null): void;
};

const DurationInput = React.forwardRef<HTMLInputElement, Props>(
  (props, ref) => {
    const { value, onChange, placeholder = '0h 00m', ...rest } = props;
    const [input, setInput] = useState('');
    const [seconds, setSeconds] = useState<number | null>(null);
    const hasError = Boolean(input && !seconds);

    useEffect(() => {
      if (value) {
        const formatted = formatDuration(value);
        setInput(formatted);
        setSeconds(value);
      }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const cleaned = sanitizeInput(raw);
      setInput(cleaned);

      const parsed = parseDuration(cleaned);
      setSeconds(parsed);

      if (onChange) {
        onChange(parsed);
      }
    };

    return (
      <div
        className={clsx(css.root, css.border, hasError && css['is-invalid'])}
      >
        <Input
          error={hasError}
          slotProps={{
            root: { className: css.wrapper },
            input: {
              ...rest,
              className: css.input,
              value: input,
              ref,
            },
          }}
          onChange={handleChange}
          placeholder={placeholder}
        />
        <FormError error={!hasError ? undefined : ''} />
      </div>
    );
  },
);

export default DurationInput;
